import { StudioSystemType } from 'types/global'
import { DropdownInput, TextFieldInput } from 'types/mounting'
import {
  defaultSnowLoadWorstCase,
  defaultWindSpeedWorstCase,
  dlubalTimeoutErrorMessage,
  fetchDlubalSnowData,
  fetchDlubalWindData,
  getDlubalCache,
} from './mounting_systems/K2US/K2Dlubal'
import {
  dlubalUKDistanceToSeaFetchFailureMessage,
  dlubalUKSnowTimeoutMessage,
  dlubalUKWindSpeedTimeoutMessage,
  dlubalUKWindTimeoutMessage,
  fetchDlubalUKData,
  getDlubalUKDistanceToSeaCache,
  getDlubalUKSnowCache,
  getDlubalUKSnowElevationCache,
  getDlubalUKWindCache,
  mapDlubalUKSnowZoneToVanDerValkIdx,
  VDV_DLUBAL_SNOW_ZONE_WORST_CASE_IDX,
  WIND_SPEED_THRESHOLD_MS,
} from './mounting_systems/ukDlubal'
import { fetchDlubalCAData, getDlubalCACache, getDlubalCASnowZoneCache, isDlubalCAMissingLocationError } from './mounting_systems/FastRack/FastRackDlubal'

type ButtonConfig = NonNullable<TextFieldInput['button']>
type DropdownButtonConfig = NonNullable<DropdownInput['button']>

export function createDlubalWindButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_us_wind',
      resultKey: 'wind_result',
      fetchFunction: fetchDlubalWindData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: defaultWindSpeedWorstCase,
      timeoutErrorMessage: dlubalTimeoutErrorMessage,
      cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed wind loading values from the calculated values from Dlubal.',
      getCacheData: () => getDlubalCache('wind') || null,
    } as any,
  }
}

export function createDlubalSnowButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_us_snow',
      resultKey: 'snow_result',
      fetchFunction: fetchDlubalSnowData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: defaultSnowLoadWorstCase,
      timeoutErrorMessage: dlubalTimeoutErrorMessage,
      cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed snow loading values from the calculated values from Dlubal.',
      getCacheData: () => getDlubalCache('snow') || null,
    } as any,
  }
}

export function createDlubalWindButtonForSolarNest(system: StudioSystemType | null | undefined): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_us_wind',
      resultKey: 'wind_result',
      fetchFunction: fetchDlubalWindData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) =>
        ['solarNestRooftop', 'solarNestGroundMount'].includes(mountingName),
      fallbackValue: 102,
      timeoutErrorMessage: dlubalTimeoutErrorMessage,
      cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed wind loading values from the calculated values from Dlubal.',
      getCacheData: () => getDlubalCache('wind') || null,
    } as any,
  }
}

export function createDlubalSnowButtonForSolarNest(system: StudioSystemType | null | undefined): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_us_snow',
      resultKey: 'snow_result',
      fetchFunction: fetchDlubalSnowData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) =>
        ['solarNestRooftop', 'solarNestGroundMount'].includes(mountingName),
      fallbackValue: 10,
      timeoutErrorMessage: dlubalTimeoutErrorMessage,
      cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed snow loading values from the calculated values from Dlubal.',
      getCacheData: () => getDlubalCache('snow') || null,
    } as any,
  }
}

export function createDlubalUKWindZoneButton(mountingNames: string[]): DropdownButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_wind',
      resultKey: 'wind_speed_ms',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: 'high',
      timeoutErrorMessage: dlubalUKWindTimeoutMessage,
      cacheIndicatorTooltip: 'Wind zone automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed the wind zone from the calculated value from Dlubal.',
      getCacheData: () => getDlubalUKWindCache() || { success: false, value: null },
      mapResultToValue: (windSpeedMs: number) => (windSpeedMs <= WIND_SPEED_THRESHOLD_MS ? 'standard' : 'high'),
    },
  }
}

/** UK Dlubal wind as numeric m/s (text field), e.g. Van der Valk. */
export function createDlubalUKWindSpeedMsButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_wind',
      resultKey: 'wind_speed_ms',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: 30.3,
      timeoutErrorMessage: dlubalUKWindSpeedTimeoutMessage,
      cacheIndicatorTooltip: 'Wind speed (m/s) automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed the wind speed from the calculated value from Dlubal.',
      getCacheData: () => getDlubalUKWindCache() || { success: false, value: null },
    } as any,
  }
}

/** UK wind cache `distance_to_sea` (km) for Van der Valk distance to shoreline fetch / reset. */
export function createDlubalUKDistanceToShorelineButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_wind',
      resultKey: 'distance_to_sea',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: 1,
      timeoutErrorMessage: dlubalUKDistanceToSeaFetchFailureMessage,
      cacheIndicatorTooltip: 'Distance to shoreline automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed the distance to shoreline from the calculated value from Dlubal.',
      getCacheData: () => getDlubalUKDistanceToSeaCache(),
    } as any,
  }
}

/** UK snow cache `elevation` (m AMSL) for Van der Valk altitude field fetch / reset. */
export function createDlubalUKSnowElevationAltitudeButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_snow',
      resultKey: 'elevation',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: 83.16,
      timeoutErrorMessage: dlubalUKSnowTimeoutMessage,
      cacheIndicatorTooltip: 'Altitude automatically populated from Dlubal load zone elevation',
      changedFromCacheTooltip: 'You have changed altitude from the value calculated from Dlubal.',
      getCacheData: () => getDlubalUKSnowElevationCache(),
    } as any,
  }
}

export function createDlubalUKSnowZoneButton(mountingNames: string[]): DropdownButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_snow',
      resultKey: 'snow_zone',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: 5,
      timeoutErrorMessage: dlubalUKSnowTimeoutMessage,
      cacheIndicatorTooltip: 'Snow zone automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed the snow zone from the calculated value from Dlubal.',
      getCacheData: () => getDlubalUKSnowCache() || { success: false, value: null },
      mapResultToValue: (snowZone: number) => snowZone,
    },
  }
}

/** UK Dlubal snow zone for Van der Valk (vendor snowZoneIdx 0–5, not MCS zone numbers 1–5). */
export function createDlubalUKSnowZoneButtonForVanDerValk(mountingNames: string[]): DropdownButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_uk_snow',
      resultKey: 'snow_zone',
      fetchFunction: fetchDlubalUKData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      fallbackValue: VDV_DLUBAL_SNOW_ZONE_WORST_CASE_IDX,
      timeoutErrorMessage: dlubalUKSnowTimeoutMessage,
      cacheIndicatorTooltip: 'Snow zone automatically populated from Dlubal weather data',
      changedFromCacheTooltip: 'You have changed the snow zone from the calculated value from Dlubal.',
      getCacheData: () => getDlubalUKSnowCache() || { success: false, value: null },
      mapResultToValue: mapDlubalUKSnowZoneToVanDerValkIdx,
    },
  }
}

export const dlubalCATimeoutErrorMessage = 'Dlubal CA fetch timed out. Please enter values manually.'

export const dlubalCALocationErrorMessage =
  'Project coordinates are not set. Set the lat and lon in Manage tab before fetching Dlubal data.'

function resolveDlubalCAFetchErrorMessage(error: unknown): string | undefined {
  if (isDlubalCAMissingLocationError(error)) {
    return dlubalCALocationErrorMessage
  }
  return undefined
}

const dlubalCAFetchErrorConfig = {
  resolveFetchErrorMessage: resolveDlubalCAFetchErrorMessage,
}

const dlubalCASnowCacheTooltips = {
  cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data (NBC 2020)',
  changedFromCacheTooltip: 'You have changed snow zone values from the calculated values from Dlubal.',
}

export function createDlubalCASnowSsButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_ca_snow',
      resultKey: 'snow_zone_1',
      fetchFunction: fetchDlubalCAData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      timeoutErrorMessage: dlubalCATimeoutErrorMessage,
      ...dlubalCAFetchErrorConfig,
      ...dlubalCASnowCacheTooltips,
      getCacheData: () => getDlubalCASnowZoneCache('ss') || { success: false, value: null },
      companionApplyFields: [
        {
          variableName: 'snowSr_kPa',
          getCacheData: () => getDlubalCASnowZoneCache('sr') || { success: false, value: null },
        },
      ],
    } as any,
  }
}

export function createDlubalCASnowSrButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_ca_snow',
      resultKey: 'snow_zone_2',
      fetchFunction: fetchDlubalCAData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      timeoutErrorMessage: dlubalCATimeoutErrorMessage,
      ...dlubalCAFetchErrorConfig,
      ...dlubalCASnowCacheTooltips,
      getCacheData: () => getDlubalCASnowZoneCache('sr') || { success: false, value: null },
      companionApplyFields: [
        {
          variableName: 'snowSs_kPa',
          getCacheData: () => getDlubalCASnowZoneCache('ss') || { success: false, value: null },
        },
      ],
    } as any,
  }
}

/** @deprecated Use createDlubalCASnowSsButton — kept for guide references. */
export function createDlubalCASnowButton(mountingNames: string[]): ButtonConfig {
  return createDlubalCASnowSsButton(mountingNames)
}

export function createDlubalCAWindButton(mountingNames: string[]): ButtonConfig {
  return {
    label: 'Fetch',
    fetchConfig: {
      dataKey: 'dlubal_ca_wind',
      resultKey: 'wind_zone_2',
      fetchFunction: fetchDlubalCAData,
      timeoutSeconds: 20,
      isValidMountingSystem: (mountingName: string) => mountingNames.includes(mountingName),
      timeoutErrorMessage: dlubalCATimeoutErrorMessage,
      ...dlubalCAFetchErrorConfig,
      cacheIndicatorTooltip: 'Value automatically populated from Dlubal weather data (NBC 2020)',
      changedFromCacheTooltip: 'You have changed wind pressure from the calculated value from Dlubal.',
      getCacheData: () => getDlubalCACache('wind') || { success: false, value: null },
    } as any,

  }
}
